import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import TransmittalWatermark from './TransmittalWatermark';

/**
 * Letter of Transmittal (Material Transfer) - a rebuild of the "Print" sheet of BU28-SOP-200.
 *
 * GEOMETRY. The source is a uniform grid: every column is 2.5 characters wide and every row
 * 15pt tall, with the printed form spanning columns B..AI. Here that grid is 36 equal columns
 * across the 576pt usable width of LETTER portrait at 18pt margins (the page box every
 * transmittal form uses), so a cell reference on the source maps straight to `span(a, b)`.
 *
 * The form has 12 fixed line-item rows, each two source rows tall. Material Transfer items are
 * live (every Tag and Panel carrying the transmittal number), so there can be more than 12;
 * rather than truncate, each further 12 prints on another copy of the form.
 *
 * "We are sending the following" is one choice (Sending_Type), marked with a literal X in its
 * box, as the paper form does. The letterhead logos and the From: office are not placed yet
 * and print blank.
 */

const COLS = 36;
const CW = 576 / COLS;
const RH = 15;
const span = (from, to) => (to - from + 1) * CW;
const left = (col) => col * CW;

const THIN = 0.75;
const BORDER = '#000';

const s = StyleSheet.create({
  page:   { paddingTop: 18, paddingBottom: 18, paddingHorizontal: 18, fontFamily: 'Helvetica', fontSize: 10 },
  row:    { flexDirection: 'row', height: RH },
  title:  { fontSize: 12, fontFamily: 'Helvetica-Bold', textAlign: 'center', borderTopWidth: THIN, borderColor: BORDER,
            paddingTop: 8, height: RH * 2 },
  lblR:   { textAlign: 'right', paddingRight: 3, paddingTop: 2 },
  val:    { borderBottomWidth: THIN, borderColor: BORDER, paddingLeft: 3, paddingTop: 2 },
  box:    { borderWidth: THIN, borderColor: BORDER, textAlign: 'center', fontFamily: 'Helvetica-Bold', paddingTop: 1 },
  boxLbl: { paddingLeft: 4, paddingTop: 2 },
  th:     { fontFamily: 'Helvetica-Bold', textAlign: 'center', borderTopWidth: THIN, borderBottomWidth: THIN,
            borderLeftWidth: THIN, borderColor: BORDER, paddingTop: 2 },
  td:     { borderBottomWidth: THIN, borderLeftWidth: THIN, borderColor: BORDER, paddingHorizontal: 3, paddingTop: 8,
            height: RH * 2 },
  ack:    { fontSize: 9, textAlign: 'justify', borderWidth: THIN, borderColor: BORDER, padding: 4 },
  sigLbl: { fontSize: 10, borderTopWidth: THIN, borderLeftWidth: THIN, borderColor: BORDER, paddingLeft: 3, paddingTop: 2,
            height: RH * 3 },
});

export const SENDING_TYPES = [
  'Shop Drawings', 'Prints', 'Specifications', 'Equipment',
  'Plans', 'Change Order', 'Copy of Letter', 'Other',
];

const ITEM_ROWS = 12;

const ACK_TEXT =
  'The receiving contractor acknowledges that the material is in factory new and un-altered conditions '
  + 'with no visible signs of damage.  Field modifications and or alterations to the material(s) transferred '
  + 'are done at the receiving contractors risk and expense to repair and/or replace and the cost of such '
  + 'shall include the necessary time by Wunderlich-Malec Systems employees to investigate, coordinate and '
  + 'conduct work associated with such repairs or replacement.  All damage to the material caused by '
  + 'rigging, relocation and field conditions will become the responsibility of the receiving party.';

const Blank = ({ rows = 1 }) => <View style={{ height: RH * rows }} />;

// Header pair: label right-aligned in B..I, value underlined in J..Y; right side label Z..AB,
// value AC..AI.
const HeaderRow = ({ lLabel, lValue, rLabel, rValue }) => (
  <View style={s.row}>
    <View style={{ width: left(1) }} />
    <Text style={[s.lblR, { width: span(1, 8) }]}>{lLabel}</Text>
    <Text style={[s.val, { width: span(9, 24) }]}>{lValue || ' '}</Text>
    <Text style={[s.lblR, { width: span(25, 27) }]}>{rLabel}</Text>
    <Text style={[s.val, { width: span(28, 34) }]}>{rValue || ' '}</Text>
  </View>
);

// A row of four category boxes at columns B, L, U, AD, each followed by its caption.
const SendingRow = ({ labels, chosen }) => {
  const at = [[1, 10], [11, 19], [20, 28], [29, 34]];
  return (
    <View style={s.row}>
      <View style={{ width: left(1) }} />
      {labels.map((label, i) => {
        const [boxCol, endCol] = at[i];
        return (
          <React.Fragment key={label}>
            <Text style={[s.box, { width: CW, height: RH }]}>{chosen === label ? 'X' : ' '}</Text>
            <Text style={[s.boxLbl, { width: span(boxCol + 1, endCol) }]}>{label}</Text>
          </React.Fragment>
        );
      })}
    </View>
  );
};

const ITEM_COLS = [
  { key: null,          head: '#',             w: span(1, 2), align: 'center' },
  { key: 'tag',         head: 'Tag Name',      w: span(3, 8) },
  { key: 'description', head: 'Description',   w: span(9, 29) },
  { key: 'serial',      head: 'Serial Number', w: span(30, 34) },
];

const FormPage = ({ data, items, offset, watermark }) => (
  <Page size="LETTER" orientation="portrait" style={s.page}>
    <TransmittalWatermark text={watermark} />

    {/* Rows 1-2: logo (A..G, not placed yet) | title H..AC | logo (AD..AJ, not placed yet). */}
    <View style={{ flexDirection: 'row', height: RH * 2 }}>
      <View style={{ width: span(0, 6) }} />
      <Text style={[s.title, { width: span(7, 28) }]}>Letter of Transmittal</Text>
      <View style={{ width: span(29, 35) }} />
    </View>
    <Blank />

    <HeaderRow lLabel="Project Title: "     lValue={data.projectTitle}      rLabel="To:"   rValue={data.to} />
    <HeaderRow lLabel="Project Number: "    lValue={data.projectNumber}     rLabel="From:" rValue={data.from} />
    <HeaderRow lLabel="Transmittal Number:" lValue={data.transmittalNumber} rLabel="Date:" rValue={data.date} />
    <Blank />

    <View style={s.row}>
      <View style={{ width: left(1) }} />
      <Text style={{ paddingTop: 2 }}>We are sending the following:</Text>
    </View>
    <Blank />
    <SendingRow labels={SENDING_TYPES.slice(0, 4)} chosen={data.sendingType} />
    <Blank />
    <SendingRow labels={SENDING_TYPES.slice(4)} chosen={data.sendingType} />
    <Blank />

    {/* Line items: header then 12 fixed rows, each two source rows tall; blank rows stay ruled. */}
    <View style={s.row}>
      <View style={{ width: left(1) }} />
      {ITEM_COLS.map((c, i) => (
        <Text key={c.head}
          style={[s.th, { width: c.w }, i === ITEM_COLS.length - 1 ? { borderRightWidth: THIN } : null]}>
          {c.head}
        </Text>
      ))}
    </View>
    {Array.from({ length: ITEM_ROWS }, (_, r) => {
      const item = items[r] || {};
      return (
        <View style={{ flexDirection: 'row' }} key={r}>
          <View style={{ width: left(1) }} />
          {ITEM_COLS.map((c, i) => (
            <Text key={c.head}
              style={[s.td, { width: c.w },
                      c.align === 'center' ? { textAlign: 'center' } : null,
                      i === ITEM_COLS.length - 1 ? { borderRightWidth: THIN } : null]}>
              {c.key ? (item[c.key] || ' ') : String(offset + r + 1)}
            </Text>
          ))}
        </View>
      );
    })}
    <Blank />

    <View style={{ flexDirection: 'row' }}>
      <View style={{ width: left(1) }} />
      <Text style={[s.ack, { width: span(1, 34), minHeight: RH * 6 }]}>{ACK_TEXT}</Text>
    </View>
    <Blank />

    {/* Signed by hand after printing, so these print as captioned boxes with no values. */}
    <View style={{ flexDirection: 'row' }}>
      <View style={{ width: left(1) }} />
      <Text style={[s.sigLbl, { width: span(1, 15) }]}>Received by:</Text>
      <Text style={[s.sigLbl, { width: span(16, 30) }]}>Company:</Text>
      <Text style={[s.sigLbl, { width: span(31, 34), borderRightWidth: THIN }]}>Date:</Text>
    </View>
  </Page>
);

const LetterOfTransmittalPdfDocument = ({ data = {}, watermark = null }) => {
  const all = data.items || [];
  const pages = [];
  for (let i = 0; i < Math.max(all.length, 1); i += ITEM_ROWS) pages.push(i);

  return (
    <Document title={`Letter of Transmittal ${data.transmittalNumber || ''}`.trim()}>
      {pages.map(offset => (
        <FormPage key={offset} data={data} offset={offset}
          items={all.slice(offset, offset + ITEM_ROWS)} watermark={watermark} />
      ))}
    </Document>
  );
};

export default LetterOfTransmittalPdfDocument;
